import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { ArrowRightIcon, XIcon } from '@repo/icons';
import { Button, InfoCard, Input } from '@repo/ui';
import type { KeyboardEvent } from 'react';

import { AuthModalException, AuthModalError } from '@features/auth/hooks/useAuthModal';
import { selectStatus, useAuthStore } from '@features/auth/stores/useAuthStore';

interface Props {
  testId: TestId;
  email: string;
  setEmail: (value: string) => void;
  submitEmail: (email?: string) => Promise<unknown>;
  requesting: boolean;
  initialEmail?: string;
  onClose?: () => void;
}

export default function EmailSection({
  testId,
  email,
  setEmail,
  submitEmail,
  requesting,
  initialEmail,
  onClose,
}: Props) {
  const authStatus = useAuthStore(selectStatus);

  const handleSubmit = async () => {
    if (requesting) return;
    try {
      await submitEmail();
    } catch (error) {
      if (error instanceof AuthModalException) {
        if (error.type === AuthModalError.InvalidEmail) {
          toast.danger(tr('auth:login_drawer.invalid_email_error'), testId.append('invalidEmailToast'));
        } else if (error.type === AuthModalError.ResendCooldownActive) {
          toast.danger(
            tr('auth:login_drawer.resend_cooldown_error', {
              email: error.context?.email,
              count: Number(error.context?.seconds),
            }),
            testId.append('resendCooldownToast')
          );
        } else {
          toast.danger(tr('auth:login_drawer.request_code_error'), testId.append('requestErrorToast'));
        }
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleSubmit();
  };

  return (
    <>
      <div className="flex justify-between">
        <h2 className="text-heading text-xl font-semibold">{tr('auth:login_drawer.email.title')}</h2>
        {onClose && (
          <button aria-label={tr('actions.close')} className="cursor-pointer" type="button" onClick={onClose}>
            <XIcon size={20} />
          </button>
        )}
      </div>

      {authStatus === 'guest' && (
        <InfoCard title={tr('auth:login_drawer.email.guest_info.title')} testId={testId.append('infoForGuests')}>
          <div className="flex flex-col gap-0.5 text-sm font-light">
            <div>{tr('auth:login_drawer.email.guest_info.description')}</div>
            <div>{tr('auth:login_drawer.email.guest_info.features')}</div>
            <div className="font-normal">{tr('auth:login_drawer.email.guest_info.invitation')}</div>
          </div>
        </InfoCard>
      )}

      <p className="text-body text-sm">{tr('auth:login_drawer.email.description')}</p>

      <Input
        testId={testId.append('email')}
        placeholder={tr('auth:login_drawer.email.email_placeholder')}
        value={email}
        onChange={setEmail}
        onKeyDown={handleKeyDown}
        type="email"
        autoFocus={!initialEmail}
      />
      <p className="text-body text-xs">{tr('auth:login_drawer.email.email_helper')}</p>

      <Button
        color="brand"
        testId={testId.append('submitEmail')}
        onClick={handleSubmit}
        rightIcon={ArrowRightIcon}
        disabled={requesting || !email.length}
      >
        {tr('auth:login_drawer.email.cta')}
      </Button>
    </>
  );
}
